import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import {
  SentimentState,
  SentimentKpisData,
  SentimentRatingTrendItem,
  SentimentReviewDepthData,
  SentimentByProductTypeItem,
  SentimentReviewsByStrainItem,
  SentimentRatingsByStrainItem,
  SentimentAttributeProfileData,
} from "./types";

export const sentimentInitialState: SentimentState = {
  loadingKpis: false,
  loadingRatingTrend: false,
  loadingReviewsByStrain: false,
  loadingByProductType: false,
  loadingRatingsByStrain: false,
  loadingAttributeProfile: false,
  loadingReviewDepth: false,
  kpis: null,
  ratingTrend: null,
  reviewsByStrain: null,
  byProductType: null,
  ratingsByStrain: null,
  attributeProfile: null,
  reviewDepth: null,
  error: null,
};

const sentimentSlice = createSlice({
  name: "analyticsSentiment",
  initialState: sentimentInitialState,
  reducers: {
    setKpisLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingKpis = action.payload;
    },
    setKpisSuccess: (state, action: PayloadAction<SentimentKpisData>) => {
      state.kpis = action.payload;
      state.loadingKpis = false;
      state.error = null;
    },
    setRatingTrendLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingRatingTrend = action.payload;
    },
    setRatingTrendSuccess: (
      state,
      action: PayloadAction<SentimentRatingTrendItem[]>,
    ) => {
      state.ratingTrend = action.payload;
      state.loadingRatingTrend = false;
      state.error = null;
    },
    setReviewsByStrainLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingReviewsByStrain = action.payload;
    },
    setReviewsByStrainSuccess: (
      state,
      action: PayloadAction<SentimentReviewsByStrainItem[]>,
    ) => {
      state.reviewsByStrain = action.payload;
      state.loadingReviewsByStrain = false;
      state.error = null;
    },
    setByProductTypeLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingByProductType = action.payload;
    },
    setByProductTypeSuccess: (
      state,
      action: PayloadAction<SentimentByProductTypeItem[]>,
    ) => {
      state.byProductType = action.payload;
      state.loadingByProductType = false;
      state.error = null;
    },
    setRatingsByStrainLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingRatingsByStrain = action.payload;
    },
    setRatingsByStrainSuccess: (
      state,
      action: PayloadAction<SentimentRatingsByStrainItem[]>,
    ) => {
      state.ratingsByStrain = action.payload;
      state.loadingRatingsByStrain = false;
      state.error = null;
    },
    setAttributeProfileLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingAttributeProfile = action.payload;
    },
    setAttributeProfileSuccess: (
      state,
      action: PayloadAction<SentimentAttributeProfileData>,
    ) => {
      state.attributeProfile = action.payload;
      state.loadingAttributeProfile = false;
      state.error = null;
    },
    setReviewDepthLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingReviewDepth = action.payload;
    },
    setReviewDepthSuccess: (
      state,
      action: PayloadAction<SentimentReviewDepthData>,
    ) => {
      state.reviewDepth = action.payload;
      state.loadingReviewDepth = false;
      state.error = null;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setKpisLoading,
  setKpisSuccess,
  setRatingTrendLoading,
  setRatingTrendSuccess,
  setReviewsByStrainLoading,
  setReviewsByStrainSuccess,
  setByProductTypeLoading,
  setByProductTypeSuccess,
  setRatingsByStrainLoading,
  setRatingsByStrainSuccess,
  setAttributeProfileLoading,
  setAttributeProfileSuccess,
  setReviewDepthLoading,
  setReviewDepthSuccess,
  setError,
} = sentimentSlice.actions;

export default sentimentSlice.reducer;
